import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Combobox, type ComboboxOption } from '../../components/Combobox';
import { useDebounced } from '../../lib/useDebounced';
import type { CustomerListQuery } from './api';
import { customerOption } from './customerOption';
import { useCustomerLookup } from './queries';

interface CustomerComboboxProps {
  id?: string;
  ariaLabel?: string;
  value: ComboboxOption | null;
  onChange: (option: ComboboxOption | null) => void;
  /** Nur Kunden dieses Standes -- beim Ticketanlegen ``active``. */
  status?: CustomerListQuery['status'];
  /** Schon gewaehlte Kunden, die nicht noch einmal erscheinen sollen. */
  excludeIds?: string[];
  placeholder?: string;
  disabled?: boolean;
  clearable?: boolean;
}

/**
 * Die Kundenauswahl -- mit Suche beim Server.
 *
 * **Nie bei der ersten Seite abgeschnitten.** Die Aufklapplisten davor
 * zeigten 25, 100 oder 200 Kunden, je nach Maske; wer dahinter lag, liess
 * sich nicht waehlen. Hier fragt jede Eingabe den Server nach Name und
 * Kundennummer, Teiltreffer genuegen.
 *
 * **Inaktive Kunden sind gekennzeichnet, nicht versteckt** -- ausser der
 * Aufrufer schraenkt mit ``status`` ein. Beim Ticketanlegen tut er das; in
 * einer Regel oder einem Formular kann ein ruhender Kunde gemeint sein.
 */
export function CustomerCombobox({
  id,
  ariaLabel,
  value,
  onChange,
  status,
  excludeIds = [],
  placeholder,
  disabled,
  clearable,
}: CustomerComboboxProps) {
  const { t } = useTranslation();
  const [term, setTerm] = useState('');
  // Erst beim ersten Oeffnen fragen -- siehe ``useCustomerLookup``.
  const [opened, setOpened] = useState(false);
  const search = useDebounced(term);
  const customers = useCustomerLookup(search, status, opened);

  const options = (customers.data?.items ?? [])
    .filter((customer) => !excludeIds.includes(customer.id))
    .map((customer) => customerOption(customer, t(`customerStatus.${customer.status}`)));

  return (
    <Combobox
      id={id}
      ariaLabel={ariaLabel}
      value={value}
      onChange={onChange}
      options={options}
      onSearch={(next) => {
        setOpened(true);
        setTerm(next);
      }}
      loading={customers.isFetching}
      placeholder={placeholder ?? t('combobox.searchCustomer')}
      disabled={disabled}
      clearable={clearable}
    />
  );
}
